import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, limit, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { 
  FaArrowRight, 
  FaRobot,
  FaBook,
  FaInstagram
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import LatestBlogs from '../components/LatestBlogs';
import LatestAutomations from '../components/LatestAutomations';

function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [latestQuestions, setLatestQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestContent = async () => {
      try {
        setLoading(true);
        const questionsQuery = query(
          collection(db, 'questions'),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        const questionsSnap = await getDocs(questionsQuery);
        setLatestQuestions(
          questionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        );
      } catch (error) {
        console.error('İçerik yükleme hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestContent();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('tr-TR');
    }
    
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('tr-TR');
    }
    
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleDateString('tr-TR');
    }

    return '';
  };

  const handleAskQuestion = () => {
    if (user) {
      navigate('/qa/new');
    } else {
      navigate('/qa/ask');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white py-24">
        <div className="absolute inset-0 opacity-10" style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h60v60H0z'/%3E%3Cpath d='M30 30h30v30H30z' fill='currentColor' opacity='.05'/%3E%3Cpath d='M0 0h30v30H0z' fill='currentColor' opacity='.05'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 relative">
              <span className="relative inline-block">
                <span className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 blur-xl opacity-30"></span>
                <span className="relative bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 text-transparent bg-clip-text">
                  EXCEL
                </span>
              </span>
              <span className="mx-4 text-white">|</span>
              <span className="relative inline-block">
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 blur-xl opacity-30"></span>
                <span className="relative bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 text-transparent bg-clip-text">
                  VADİSİ
                </span>
              </span>
            </h1>
            <p className="text-lg md:text-xl text-blue-100 mb-6 max-w-3xl mx-auto">
              Excel ve VBA dünyasında profesyonel çözümler.
              İşlerinizi otomatikleştirin, zamandan tasarruf edin.
            </p>
            <a
              href="https://www.instagram.com/excelvbamakro/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 group"
            >
              <FaInstagram className="mr-2 text-xl group-hover:scale-110 transition-transform" />
              Instagram'da Takip Et
            </a>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 via-gray-50/80 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4">
        {/* Excel Topluluğu ve Son İçerikler Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 -mt-8 relative z-10">
          {/* Excel Topluluğu */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1 bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 border border-blue-100/50"
          >
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4 relative">
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 blur-lg opacity-20"></span>
                <span className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  EXCEL TOPLULUĞU
                </span>
              </h2>
              <div className="h-1 w-24 mx-auto bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full mb-6"></div>
              <p className="text-gray-600 mb-6">
                Excel ve VBA ile ilgili sorularınızı sorun, deneyimli kullanıcılardan anında yanıt alın.
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleAskQuestion}
                  className="w-full group relative px-6 py-3 overflow-hidden rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 text-white"
                >
                  <span className="relative flex items-center justify-center">
                    SORU SOR
                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                <Link
                  to="/qa"
                  className="w-full group relative px-6 py-3 overflow-hidden rounded-xl border-2 border-blue-400 hover:border-blue-500 transition-colors text-center"
                >
                  <span className="text-blue-600 font-medium">TÜM SORULAR</span>
                </Link>
              </div>
            </div>

            {/* Son Sorular */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Son Sorular</h3>
              <div className="space-y-4">
                {latestQuestions.map(question => (
                  <Link
                    key={question.id}
                    to={`/qa/${question.id}`}
                    className="block bg-blue-50 rounded-xl p-4 hover:bg-blue-100 transition-colors"
                  >
                    <p className="text-gray-800 font-medium line-clamp-2 mb-2">{question.question}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{question.author}</span>
                      <span>{formatDate(question.createdAt)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Son İçerikler */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-emerald-100/50"
            >
              <h2 className="text-3xl font-bold mb-6 relative text-center">
                <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 blur-lg opacity-20"></span>
                <span className="relative bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 bg-clip-text text-transparent">
                  SON OTOMASYONLAR
                </span>
              </h2>
              <LatestAutomations />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-blue-100/50"
            >
              <h2 className="text-3xl font-bold mb-6 relative text-center">
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 blur-lg opacity-20"></span>
                <span className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  SON BLOG YAZILARI
                </span>
              </h2>
              <LatestBlogs />
            </motion.div>

            <div className="flex justify-center space-x-6">
              <Link
                to="/automations"
                className="flex-1 inline-flex items-center justify-center px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <FaRobot className="mr-2" />
                Tüm Otomasyonlar
              </Link>

              <Link
                to="/blog"
                className="flex-1 inline-flex items-center justify-center px-6 py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <FaBook className="mr-2" />
                Tüm Blog Yazıları
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;